import React, { useState, useEffect } from "react";
import {
  fetchApplicationById,
  createApplication,
  updateApplication,
  fetchAllApplicationsMain,
  deleteApplication,
} from "../EditApplication/applicationApiCalls";
import { useNavigate, useLocation } from "react-router-dom";
import "./EditApplication.css";

import { clearTokens, isTokenExpired, storeLoginUrlPath, getTokenDetails } from '../../utils/authflow';

const EditApplication = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const tokenDetails = getTokenDetails();
  const [formValues, setFormValues] = useState({
    appName: "",
    appDescription: "",
    appImage: "",
    appImageFile: null,
    appInstances: [{ title: "", link: { url: "" }, instanceId: "" }],
    appStoreLink: "",
    playStoreLink: "",
    displayOrder: "",
    visibility: "",
    instanceId: ""
  });

  useEffect(() => {
    const fetchAllApplications = async () => {
      try {
        const token = tokenDetails?.accessToken?.accessToken;
        const fetchedApps = await fetchAllApplicationsMain(token);
        console.log("application count   ", fetchedApps.length);
        setApplications(fetchedApps.map((app) => app.data));
      } catch (error) {
        console.error("Error fetching applications:", error);
      }
    };

    fetchAllApplications();

    if (isTokenExpired()) {
      storeLoginUrlPath(location.pathname);
      clearTokens();
      navigate('/');
    }

  }, []);

  const handleApplicationSelect = async (id) => {
    setSelectedAppId(id);
    setIsAddMode(false);

    try {
      const token = tokenDetails?.accessToken?.accessToken;
      const fetchedApp = await fetchApplicationById(id, token);
      const processedInstances = (fetchedApp.data.app_instances || []).map(
        (instance) => {
          let additionalInfo = false;
          let link = instance.link;
          if (
            typeof instance.link === "object" &&
            instance.link !== null &&
            instance.link.method === "POST"
          ) {
            additionalInfo = true;
          }
          return {
            ...instance,
            additionalInfo,
          };
        }
      );
      setFormValues({
        appName: fetchedApp.data.title,
        appDescription: fetchedApp.data.description,
        appImage: `/ros_be_app_directory/applications/${id}/image`,
        appImageFile: null,
        appInstances: processedInstances,
        appStoreLink: fetchedApp.data.mobile_app_links?.app_store || "",
        playStoreLink: fetchedApp.data.mobile_app_links?.play_store || "",
        displayOrder: fetchedApp.display_order || "",
        visibility: fetchedApp.visibility || "",
        instanceId: fetchedApp.instanceId || ""
      });
    } catch (error) {
      console.error("Error fetching application details:", error);
    }
  };

  const handleInputChange = (e, field) => {
    setFormValues({ ...formValues, [field]: e.target.value });
  };

  const handleInstanceChange = (index, field, value) => {
    const updatedInstances = [...formValues.appInstances];
    updatedInstances[index][field] = value;
    setFormValues({ ...formValues, appInstances: updatedInstances });
  };

  const handleAddInstance = () => {
    setFormValues({
      ...formValues,
      appInstances: [
        ...formValues.appInstances,
        { title: "", link: { url: "" }, visibility: "", instanceId: "" },
      ],
    });
  };

  const handleRemoveInstance = (index) => {
    const updatedInstances = formValues.appInstances.filter(
      (_, i) => i !== index
    );
    setFormValues({ ...formValues, appInstances: updatedInstances });
  };

  const handleAdditionalInfoToggle = (index, isChecked) => {
    const updatedInstances = [...formValues.appInstances];
    updatedInstances[index].additionalInfo = isChecked;

    if (isChecked && typeof updatedInstances[index].link === "string") {
      // Convert link to an object if additional info is enabled
      updatedInstances[index].link = {
        url: updatedInstances[index].link,
        method: "POST",
      };
    } else if (!isChecked) {
      // Remove additional info fields when unchecked
      updatedInstances[index].link = updatedInstances[index].link.url || "";
    }

    setFormValues({ ...formValues, appInstances: updatedInstances });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      setFormValues({
        ...formValues,
        appImage: reader.result,
        appImageFile: file,
      });
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      alert("Failed to load the image. Please try again.");
    };

    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (
      !formValues.appName ||
      !formValues.appDescription ||
      !formValues.appImage ||
      !formValues.visibility
    ) {
      alert("Please fill out all mandatory fields.");
      return;
    }

    const formData = new FormData();

    // Build app_instances with bug fix logic (preserve additionalInfo and link structure)
    const updatedInstances = formValues.appInstances.map((instance) => {
      const { additionalInfo, link, ...rest } = instance;

      if (additionalInfo) {
        const filteredLink = {
          method: "POST",
          url: link.url || "",
        };

        if (link.stay_alive_url)
          filteredLink.stay_alive_url = link.stay_alive_url;
        if (link.secret_text) filteredLink.secret_text = link.secret_text;
        if (link.include_date !== undefined)
          filteredLink.include_date = link.include_date;
        if (link.target_query) filteredLink.target_query = link.target_query;

        return { ...rest, link: filteredLink };
      }

      return {
        ...rest,
        link: typeof link === "string" ? link : link.url,
      };
    });

    // Append JSON stringified data
    formData.append(
      "data",
      JSON.stringify({
        title: formValues.appName,
        description: formValues.appDescription,
        app_instances: updatedInstances,
        mobile_app_links: {
          app_store: formValues.appStoreLink,
          play_store: formValues.playStoreLink,
        },
      })
    );

    formData.append("display_order", Number(formValues.displayOrder));
    formData.append("created_by", "user@example.com");
    formData.append("image", formValues.appImageFile); // Image as bytes
    formData.append("visibility", formValues.visibility);

    try {
      const token = tokenDetails?.accessToken?.accessToken;
      const response = isAddMode
        ? await createApplication(formData, token)
        : await updateApplication(selectedAppId, formData, token);

      if (!response.ok) {
        throw new Error("Failed to save application.");
      }

      const responseBody = await response.json();
      if (response.ok && responseBody.application) {
        alert(
          isAddMode
            ? "Application created successfully!"
            : "Application updated successfully!"
        );

        setFormValues({
          appName: "",
          appDescription: "",
          appImageFile: null,
          appInstances: [{ title: "", link: { url: "" }, instanceId: "" }],
          appStoreLink: "",
          playStoreLink: "",
          visibility: "",
        });
        setSelectedAppId(null);
        setIsAddMode(false);
      } else {
        throw new Error(responseBody.message || "Unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving application:", error);
      alert("Failed to save the application. Please try again.");
    }
  };

  const handleCancel = () => {
    if (isAddMode) {
      setIsAddMode(false);
    } else {
      navigate("/");
    }
  };



  const handleDelete = async () => {
    if (!selectedAppId) {
      alert("Please select an application to delete.");
      return;
    }

    const confirmed = window.confirm(
      `Delete "${formValues.appName}"? This action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      setIsDeleting(true);
      const token = tokenDetails?.accessToken?.accessToken;
      const result = await deleteApplication(selectedAppId, token);
      alert(result?.message || "Application deleted successfully!");
      setApplications((prev) => prev.filter((app) => app.id !== selectedAppId));
      setFormValues({
        appName: "",
        appDescription: "",
        appImage: "",
        appImageFile: null,
        appInstances: [{ title: "", link: { url: "" }, instanceId: "" }],
        appStoreLink: "",
        playStoreLink: "",
        displayOrder: "",
        visibility: "",
        instanceId: ""
      });
      setSelectedAppId(null);
      setIsAddMode(false);
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete the application. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div className="edit-application">
      <h1>{isAddMode ? "Add New Application" : "Edit Application"}</h1>

      {!isAddMode && (
        <button
          onClick={() => {
            setFormValues({
              appName: "",
              appDescription: "",
              appImage: "",
              appInstances: [{ title: "", link: { url: "" }, instanceId: "" }],
              appStoreLink: "",
              playStoreLink: "",
              displayOrder: applications.length + 1,
              visibility: "",
            });
            setSelectedAppId(null);
            setIsAddMode(true);
          }}
        >
          Add New Application
        </button>
      )}

      {!isAddMode && (
        <div className="form-group">
          <label>Application Name *</label>
          <select data-testid="app-dropdown"
            value={selectedAppId || ""}
            onChange={(e) => handleApplicationSelect(e.target.value)}
          >
            <option value="">Select Application</option>
            {applications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.title}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Application Name *</label>
        <input data-testid="appName"
          type="text"
          placeholder="Enter Application Name"
          value={formValues.appName}
          onChange={(e) => handleInputChange(e, "appName")}
        />
      </div>

      <div className="form-group">
        <label>Application Description *</label>
        <textarea data-testid="appDescription"
          placeholder="Enter Application Description..."
          value={formValues.appDescription}
          onChange={(e) => handleInputChange(e, "appDescription")}
        />
      </div>

      <div data-testid="displayOrder" className="form-group">
        <label>Display Order {isAddMode && "(Auto-assigned)"} *</label>
        <input
          type="number"
          placeholder="Display Order"
          value={formValues.displayOrder}
          disabled={isAddMode}
          onChange={(e) => handleInputChange(e, "displayOrder")}
        />
      </div>

      <div className="form-group">
        <label>Visibility *</label>
        <select data-testid="visibility"
          id="visibility-select"
          name="visibility"
          value={formValues.visibility}
          onChange={(e) =>
            setFormValues((prev) => ({ ...prev, visibility: e.target.value }))
          }
          style={{
            padding: "0.5rem 1rem",
            fontSize: "1rem",
            borderRadius: "4px",
            border: "1px solid #ccc",
            width: "100%",
            maxWidth: "300px",
            backgroundColor: "#fff",
          }}
          required
        >
          <option value="" disabled>
            Select visibility
          </option>
          <option value="internal">Only BH Internal users</option>
          <option value="all">BH Internal & External users</option>
        </select>
      </div>

      <div className="form-group">
        <label>Application Image *</label>
        {formValues.appImage && (
          <img data-testid="appImage"
            src={formValues.appImage}
            alt="Application"
            style={{ maxWidth: "200px", marginBottom: "10px" }}
          />
        )}
        <input type="file" onChange={handleImageUpload} />
      </div>

      <div className="form-group">
        <label>Instances</label>
        {formValues.appInstances.map((instance, index) => (
          <div className="instance-card" key={index}>
            {/* Card Header */}
            <div data-testid="appInstances" className="instance-card-header">
              <button
                className="minus-instance-btn"
                onClick={() => handleRemoveInstance(index)}
                title="Remove Instance"
                style={{ visibility: index === 0 ? "hidden" : "visible" }}
              >
                -
              </button>
              <button
                className="add-instance-btn"
                onClick={handleAddInstance}
                title="Add Instance"
              >
                +
              </button>
            </div>

            {/* Card Body */}
            <div className="instance-card-body">
              <div className="instance-form-table">
                {/* Instance Title */}
                <label>Instance Title</label>
                <input data-testid="title"
                  type="text"
                  placeholder="Instance Title"
                  value={instance.title}
                  onChange={(e) =>
                    handleInstanceChange(index, "title", e.target.value)
                  }
                />

                {/* Instance URL */}
                <label>Instance URL</label>
                <input data-testid="instanceURL"
                  type="text"
                  placeholder="Instance URL"
                  value={
                    typeof instance.link === "string"
                      ? instance.link
                      : instance?.link?.url || ""
                  }

                  onChange={(e) =>
                    handleInstanceChange(
                      index,
                      "link",
                      typeof instance.link === "string"
                        ? e.target.value
                        : { ...instance.link, url: e.target.value }
                    )
                  }
                />

                {/* Instance Id */}

                <label>Instance Id</label>
                <input data-testid="instanceId"
                  type="text"
                  placeholder="Instance Id"
                  value={instance.instanceId ? instance.instanceId : ""}
                  onChange={(e) =>
                    handleInstanceChange(index, "instanceId", e.target.value)
                  }

                />

                {/* Visibility */}
                <label>Visibility *</label>
                <select data-testId="visibilityinstance"
                  id="visibility-select"
                  name="visibility"
                  value={instance.visibility || ""}
                  onChange={(e) => handleInstanceChange(index, "visibility", e.target.value)}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "1rem",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    width: "100%",
                    maxWidth: "300px",
                    backgroundColor: "#fff",
                  }}
                  required
                >
                  <option value="" disabled>
                    Select visibility
                  </option>
                  <option value="internal">Only BH Internal users</option>
                  <option value="all">BH Internal & External users</option>
                </select>

                {/* Additional Info Toggle */}
                <label>Additional Info</label>
                <input
                  type="checkbox"
                  checked={instance.additionalInfo || false}
                  onChange={(e) =>
                    handleAdditionalInfoToggle(index, e.target.checked)
                  }
                />
              </div>
            </div>

            {/* Additional Info Fields */}
            {instance.additionalInfo && (
              <div className="instance-card-body">
                <div className="instance-form-table">
                  {/* Application Type */}
                  <label>Application Type</label>
                  <input
                    type="text"
                    placeholder="Application Type"
                    value={instance.app_type || ""}
                    onChange={(e) =>
                      handleInstanceChange(index, "app_type", e.target.value)
                    }
                  />

                  {/* Stay Alive URL */}
                  <label>Stay Alive URL</label>
                  <input
                    type="text"
                    placeholder="Stay Alive URL"
                    value={instance.link.stay_alive_url || ""}
                    onChange={(e) =>
                      handleInstanceChange(index, "link", {
                        ...instance.link,
                        stay_alive_url: e.target.value,
                      })
                    }
                  />

                  {/* Secret Text */}
                  <label>Secret Text</label>
                  <input
                    type="text"
                    placeholder="Secret Text"
                    value={instance.link.secret_text || ""}
                    onChange={(e) =>
                      handleInstanceChange(index, "link", {
                        ...instance.link,
                        secret_text: e.target.value,
                      })
                    }
                  />

                  {/* Include Date */}
                  <label>Include Date</label>
                  <input
                    type="checkbox"
                    checked={instance.link.include_date || false}
                    onChange={(e) =>
                      handleInstanceChange(index, "link", {
                        ...instance.link,
                        include_date: e.target.checked,
                      })
                    }
                  />

                  {/* Target Query */}
                  <label>Target Query</label>
                  <input
                    type="text"
                    placeholder="Target Query"
                    value={instance.link.target_query || ""}
                    onChange={(e) =>
                      handleInstanceChange(index, "link", {
                        ...instance.link,
                        target_query: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="form-group">
        <label>App Store Link</label>
        <input
          type="text"
          placeholder="Enter App Store URL"
          value={formValues.appStoreLink}
          onChange={(e) => handleInputChange(e, "appStoreLink")}
        />
      </div>

      <div className="form-group">
        <label>Play Store Link</label>
        <input
          type="text"
          placeholder="Enter Play Store URL"
          value={formValues.playStoreLink}
          onChange={(e) => handleInputChange(e, "playStoreLink")}
        />
      </div>

      <div className="button-group">
        <button onClick={handleSave}>Save</button>
        <button onClick={handleCancel}>Cancel</button>
        <button
          onClick={handleDelete}
          disabled={isAddMode || !selectedAppId || isDeleting}
          title={
            isAddMode
              ? "Delete is disabled while adding a new application"
              : !selectedAppId
                ? "Select an application first"
                : isDeleting
                  ? "Deleting..."
                  : "Delete application"
          }
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>

      </div>
    </div>
  );
};

export default EditApplication;